import React, { useEffect, useState } from 'react';
import { Sparkles, Play, Loader2, AlertTriangle, ShieldAlert, Store, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../lib/api';

const RULE_LABELS = {
  REINCIDENCIA_STOCK: 'Reincidencia stock bajo',
  VISITA_CORTA: 'Visita demasiado corta',
  FOTOS_FALTANTES: 'Fotos faltantes',
  MERCADERISTA_REINCIDENTE: 'Mercaderista reincidente',
  TIENDA_CRITICA: 'Tienda crítica',
};

const sevColor = (sev) => {
  if (sev === 'CRITICAL') return 'destructive';
  if (sev === 'WARNING') return 'default';
  return 'secondary';
};

export default function RulesEnginePage() {
  const [stats, setStats] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statsRes, actionsRes] = await Promise.all([
        api.get('/rules/stats'),
        api.get('/rules/actions', { params: { limit: 50 } }),
      ]);
      setStats(statsRes.data);
      setActions(actionsRes.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo cargar el motor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await api.post('/rules/run');
      const summary = res.data?.summary || {};
      const total = Object.values(summary).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Motor ejecutado: ${total} acción(es) nuevas`);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error ejecutando motor');
    } finally {
      setRunning(false);
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6" data-testid="rules-engine-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-500 to-red-600 flex items-center justify-center">
            <ShieldAlert className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Motor Autónomo</h1>
            <p className="text-sm text-slate-500">
              Reglas determinísticas que vigilan operación 24/7
            </p>
          </div>
        </div>
        <Button
          onClick={runNow}
          disabled={running}
          data-testid="rules-run-btn"
          className="gap-2"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? 'Ejecutando…' : 'Ejecutar ahora'}
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="kpi-total-actions">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Acciones totales</p>
                <p className="text-3xl font-bold mt-2">{stats?.total_actions ?? '—'}</p>
              </div>
              <Sparkles className="w-8 h-8 text-amber-500 opacity-60" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-critical">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Críticas</p>
                <p className="text-3xl font-bold mt-2 text-red-600">
                  {stats?.by_severity?.CRITICAL ?? 0}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500 opacity-60" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-high-risk">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Merc. high-risk</p>
                <p className="text-3xl font-bold mt-2 text-orange-600">
                  {stats?.high_risk_users_count ?? 0}
                </p>
              </div>
              <User className="w-8 h-8 text-orange-500 opacity-60" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-critical-stores">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Tiendas críticas</p>
                <p className="text-3xl font-bold mt-2 text-purple-600">
                  {stats?.critical_stores_count ?? 0}
                </p>
              </div>
              <Store className="w-8 h-8 text-purple-500 opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="actions">
        <TabsList>
          <TabsTrigger value="actions" data-testid="tab-actions">Acciones recientes</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">Por regla</TabsTrigger>
          <TabsTrigger value="risk" data-testid="tab-risk">High risk</TabsTrigger>
          <TabsTrigger value="stores" data-testid="tab-stores">Tiendas críticas</TabsTrigger>
        </TabsList>

        <TabsContent value="actions">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Últimas {actions.length} acciones</CardTitle>
            </CardHeader>
            <CardContent>
              {actions.length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center">
                  {loading ? 'Cargando…' : 'No hay acciones todavía. Haz click en "Ejecutar ahora".'}
                </p>
              ) : (
                <div className="space-y-2" data-testid="actions-list">
                  {actions.map((a) => (
                    <div
                      key={a.id}
                      className="border border-slate-200 rounded-md p-3 flex items-start justify-between gap-3 hover:bg-slate-50"
                      data-testid={`action-${a.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge variant={sevColor(a.severity)} className="uppercase">
                            {a.severity}
                          </Badge>
                          <Badge variant="outline">
                            {RULE_LABELS[a.rule_type] || a.rule_type}
                          </Badge>
                          <span className="text-xs text-slate-400">{formatDate(a.created_at)}</span>
                        </div>
                        <p className="text-sm font-medium text-slate-800">{a.title}</p>
                        <p className="text-xs text-slate-500 mt-1">{a.description}</p>
                        {a.action_taken && (
                          <p className="text-xs text-blue-600 mt-1">→ {a.action_taken}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Acciones por regla</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(stats?.by_rule_type || {}).map(([type, count]) => (
                  <div
                    key={type}
                    className="flex items-center justify-between border-b border-slate-100 py-2"
                    data-testid={`rule-stat-${type}`}
                  >
                    <span className="text-sm">{RULE_LABELS[type] || type}</span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
                {Object.keys(stats?.by_rule_type || {}).length === 0 && (
                  <p className="text-sm text-slate-500 py-4 text-center">Sin datos todavía.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="risk">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Mercaderistas high-risk</CardTitle>
            </CardHeader>
            <CardContent>
              {(stats?.high_risk_users || []).length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">
                  Ningún mercaderista marcado como high-risk.
                </p>
              ) : (
                <div className="space-y-2" data-testid="high-risk-list">
                  {stats.high_risk_users.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between border-b border-slate-100 py-2"
                    >
                      <div>
                        <p className="font-medium text-sm">{u.name}</p>
                        <p className="text-xs text-slate-500">{u.phone_e164}</p>
                      </div>
                      <Badge variant="destructive">HIGH RISK</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stores">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tiendas críticas</CardTitle>
            </CardHeader>
            <CardContent>
              {(stats?.critical_stores || []).length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">
                  Ninguna tienda marcada como crítica.
                </p>
              ) : (
                <div className="space-y-2" data-testid="critical-stores-list">
                  {stats.critical_stores.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between border-b border-slate-100 py-2"
                    >
                      <div>
                        <p className="font-medium text-sm">{s.name}</p>
                        <p className="text-xs text-slate-500">{s.code} · {s.address}</p>
                      </div>
                      <Badge variant="destructive">CRÍTICA</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
